import moment from "moment";

export const isTodayDate = (date: Date | string) =>
  moment().format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD");

export const isTomorrowDate = (date: Date | string) =>
  moment().add(1, "day").format("YYYY-MM-DD") ===
  moment(date).format("YYYY-MM-DD");

export const isBeforeToday = (date: Date | string) => {
  const d = moment(date);
  const today = moment().startOf("day");
  return d.isBefore(today);
};

export const getCurrentDate = () => moment().format("YYYY-MM-DD");
export const getCurrentDateYearShort = () => moment().format("YY-MM-DD");
export const getCurrentTime = () => moment().format("HH:mm:ss");

export const dateToString = (
  date?: Date | null | string,
  invalidDateLabel = "",
) => {
  if (!date) return invalidDateLabel;
  const formatted = moment(date).format("DD-MM-YYYY");
  if (formatted === "Invalid date") return invalidDateLabel || formatted;
  return formatted;
};

export const parseDate = (date?: Date | string | null, dateFormat?: string) => {
  if (!date) return null;
  const parsed =
    typeof date === "string" && dateFormat
      ? moment(date, dateFormat, true)
      : moment(date);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const dateForDatabase = (date?: Date | string | null) =>
  date ? moment(date).format("YYYY-MM-DD") : "";

export const dateAndTimeForDatabase = (date?: Date | string | null) =>
  date ? moment(date).format("YYYY-MM-DD HH:mm") : "";

export const ceilToMinute = (date?: Date | string | null) => {
  if (!date) return undefined;
  const parsedDate = moment(date);
  return parsedDate.isValid()
    ? new Date(Math.ceil(parsedDate.valueOf() / 60_000) * 60_000)
    : undefined;
};

export const getDateAndTime = (
  date?: Date | null | string,
  invalidDateLabel = "",
) => {
  if (!date) return invalidDateLabel;
  const formatted = moment(date).format("DD-MM-YYYY HH:mm");
  if (formatted === "Invalid date") return invalidDateLabel || formatted;
  return formatted;
};

export const getHoursAndMinutes = (date?: Date | null) =>
  date ? moment(date).format("HH:mm") : "";

export const getWeekDays = (locale: string) => {
  const baseDate = new Date(Date.UTC(2017, 0, 2));
  const weekDays: { id: number; name: string }[] = [];
  for (let i = 1; i <= 7; i++) {
    weekDays.push({
      id: i,
      name: baseDate.toLocaleDateString(locale, { weekday: "long" }),
    });
    baseDate.setDate(baseDate.getDate() + 1);
  }
  return weekDays;
};

export const getDateWithoutNthMonths = (months: number) =>
  new Date(new Date().setMonth(new Date().getMonth() - months));

export const getDateWithoutNthDays = (days: number) =>
  moment().subtract(days, "days").toDate();

export const getStartFromSpecificDate = (date?: Date | null | string) =>
  moment(date).toDate();

export const getLastDayOfMonth = (
  date: Date | string,
  withFullDate?: boolean,
) => {
  const mask = withFullDate ? "YYYY-MM-DD" : "DD";
  return moment(date).endOf("month").format(mask);
};

export const getFirstDayOfMonth = (
  date: Date | string,
  withFullDate?: boolean,
) => {
  const mask = withFullDate ? "YYYY-MM-DD" : "DD";
  return moment(date).startOf("month").format(mask);
};

export const getDateDifference = (
  comparingDate: Date | string,
  currentDate?: Date | string,
  comparator: "months" | "days" = "months",
) => {
  const currentMoment = moment(currentDate);
  const comparingMoment = moment(comparingDate);
  return comparingMoment.diff(currentMoment, comparator);
};

export const getCurrentYearOrMonth = (
  type: "year" | "month",
  subtract?: number,
) => {
  const d = moment();
  if (subtract) d.subtract(subtract, type);
  return type === "year" ? d.year() : d.month();
};

export const getYearFromDate = (date: Date | string) =>
  moment(date).format("YYYY");

export const getDaysInYear = (year: number) => {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return null;
  return moment({ year }).isLeapYear() ? 366 : 365;
};

export const getDateFromJulianDay = (year: number, day: number) => {
  const daysInYear = getDaysInYear(year);
  if (!daysInYear || !Number.isInteger(day) || day < 0 || day >= daysInYear) {
    return null;
  }
  return moment({ year, month: 0, date: 1 }).add(day, "days").toDate();
};

// AWB/BIN use PHP's date('z'): January 1 is day 000.
export const getJulianDateInfo = (date?: Date | string | null) => {
  if (!date) return null;
  const parsed = moment(date);
  if (!parsed.isValid()) return null;
  const day = parsed.dayOfYear() - 1;
  const formattedDay = String(day).padStart(3, "0");
  return {
    year: parsed.year(),
    day,
    formattedDay,
    code: `${parsed.format("YY")}${formattedDay}`,
  };
};

export const getDaysInMonth = (
  month?: number | string,
  year?: number | string,
) => {
  const m = month ? Number(month) - 1 : moment().month();
  const y = year ? Number(year) : moment().year();
  const daysInMonth = moment({ year: y, month: m }).daysInMonth();
  const days: string[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(moment({ year: y, month: m, day }).format("DD"));
  }
  return days;
};

export const getCurrentDay = () => new Date().getDate();
export const getCurrentMonth = () => new Date().getMonth() + 1;
export const getCurrentYear = () => new Date().getFullYear();

export const getDaysOfMonthBeforeToday = (
  month?: number | string,
  year?: number | string,
) => {
  const days = getDaysInMonth(month, year);
  const currentDay = getCurrentDay();
  const currentMonth = getCurrentMonth();
  const currentYear = getCurrentYear();
  const targetMonth = month ? Number(month) : currentMonth;
  const targetYear = year ? Number(year) : currentYear;
  if (targetMonth === currentMonth && targetYear === currentYear) {
    return days.filter((d) => Number(d) < currentDay);
  }
  return days;
};

export const monthsDataDropdownValue = moment
  .monthsShort()
  .map((label: string, i: number) => ({
    id: i + 1,
    label,
  }));

export const getMonthsBeforeToday = (year?: number | string) => {
  const curMonth = getCurrentMonth();
  const curYear = getCurrentYear();
  if (year && Number(year) === curYear) {
    return monthsDataDropdownValue.filter((m) => Number(m.id) <= curMonth);
  }
  return monthsDataDropdownValue;
};

export function getTodayInfo() {
  const today = moment();
  return {
    dayOfYear: today.dayOfYear(),
    formattedDate: today.format("DD-MM-YYYY"),
  };
}

export const getDayFromNumber = (isoWeekday: number) =>
  moment().isoWeekday(isoWeekday).format("dd");

/* === Grupări utile pentru importuri scurte === */
export const dateChecks = {
  isTodayDate,
  isTomorrowDate,
  isBeforeToday,
};

export const now = {
  getCurrentDate,
  getCurrentDateYearShort,
  getCurrentTime,
};

export const format = {
  dateToString,
  dateForDatabase,
  dateAndTimeForDatabase,
  getDateAndTime,
  getHoursAndMinutes,
  getYearFromDate,
};

export const ranges = {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getDaysInMonth,
  getDaysOfMonthBeforeToday,
  getMonthsBeforeToday,
  monthsDataDropdownValue,
};

export const math = {
  getDateFromJulianDay,
  getJulianDateInfo,
  getDaysInYear,
  ceilToMinute,
  parseDate,
  getDateDifference,
  getDateWithoutNthMonths,
  getDateWithoutNthDays,
  getStartFromSpecificDate,
};

export const parts = {
  getCurrentDay,
  getCurrentMonth,
  getCurrentYear,
  getCurrentYearOrMonth,
  getDayFromNumber,
};

export const locale = {
  getWeekDays,
};
